import { useCallback, useEffect, useState } from 'react';
import { fetchPatientList, fetchPatientsCount } from 'pages/Dashboard/services/patient.services';
import { useQuery } from 'react-query';
import { patientUrls } from 'utils/apiUrls';
import { PatientObj } from 'pages/Dashboard/types/patient.types';
import { PATIENT_LIST_VIEW } from 'pages/Dashboard/enums';
import { useHttp } from 'hooks/use-fetch';

export interface TFilter {
  status?: string;
  lastName?: string;
}

export type PatientsCount = { [key in PATIENT_LIST_VIEW]: number };

export const getAlphabetFilterList = (patients: PatientObj[]): string[] => {
  const patientNameFilterSet = new Set<string>();
  if (patients?.length > 0) {
    patients.forEach((p) => {
      if ((p.name?.lastName?.length ?? 0) > 0) {
        const firstChar = p.name?.lastName?.charAt(0).toLocaleUpperCase() ?? '';
        patientNameFilterSet.add(firstChar);
      }
    });
    return [...patientNameFilterSet];
  }
  return [];
};

export const getFilteredList = (allPatients: PatientObj[], lastName?: string) => {
  let filteredPatients = [...allPatients];
  if (lastName) {
    filteredPatients = filteredPatients
      .filter(
        ({ name }) => name?.lastName?.charAt(0)?.toLocaleUpperCase()
          === lastName?.toUpperCase(),
      );
  }
  return {
    filteredPatients,
  };
};

export default function usePatientList(
  providerId: string,
  filters?: TFilter,
  queryKey?: string | null,
  searchQuery?: string,
  sortDirection?: string | null,
  sortField?: string | null,
  offset?: number | null,
  limit?: number | null,
  providerIdFilter?: string,
) {
  const [patientList, setPatientList] = useState<PatientObj[]>([]);
  const [filteredPatientList, setFilteredPatientList] = useState<PatientObj[]>([]);
  const [demoPatientsCount, setDemoPatientsCount] = useState<PatientsCount>({} as PatientsCount);
  const [patientsCount, setPatientsCount] = useState({} as Record<string, number>);
  const { http } = useHttp();

  const { isFetching: isFetchingPatientsCount, refetch: refetchPatientsCount } = useQuery(
    [patientUrls.patientsCount.queryUrl, providerId, searchQuery, providerIdFilter, queryKey],
    {
      queryFn: fetchPatientsCount(http.get, true),
      enabled: !!providerId && !!filters?.status,
      refetchOnMount: 'always',
      onSuccess({ statusCounts }) {
        const patientsCountMap = statusCounts?.reduce((acc, { status, count }) => {
          if (patientsCount[status] === undefined) {
            acc[`${status}-initial`] = count;
          }
          acc[status] = count;
          return acc;
        }, {} as Record<string, number>);
        setPatientsCount((prev) => ({ ...prev, ...patientsCountMap }));
      },
    },
  );
  const {
    isFetching: isFetchingPatientsList,
    isLoading: isLoadingPatientsList,
    isError: isErrorPatientsList,
    refetch: refetchPatientsList,
  } = useQuery(
    [patientUrls.listPatients.queryUrl,
      providerId,
      filters?.status,
      searchQuery,
      sortDirection,
      sortField,
      offset,
      limit,
      providerIdFilter],
    {
      queryFn: fetchPatientList(http.get, true),
      enabled:
        !!providerId && !!limit,
      refetchOnMount: 'always',
      onSuccess({ patients }) {
        const demoPatientCount = patients.filter((p) => p.demoPatient).length;
        setDemoPatientsCount((prev) => {
          const statusKey = filters?.status as PATIENT_LIST_VIEW;
          if (statusKey && prev[statusKey] === undefined) {
            return ({ ...prev, [statusKey]: demoPatientCount });
          }
          return prev;
        });
        setPatientList(patients);
      },
    },
  );

  const updateFilters = useCallback((updatedPatients: PatientObj[], lastName?: string) => {
    const { filteredPatients } = getFilteredList(updatedPatients, lastName);
    setFilteredPatientList(filteredPatients);
  }, []);

  useEffect(() => {
    updateFilters(patientList, filters?.lastName);
  }, [filters?.lastName, patientList, updateFilters]);

  return {
    isFetching: isFetchingPatientsList || isFetchingPatientsCount,
    isError: isErrorPatientsList,
    patientList,
    filteredPatientList,
    patientsCount,
    demoPatientsCount,
    isLoadingPatientsList,
    refetchPatientsCount,
    refetchPatientsList,
  };
}
